import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["onClick"]

import { Icon } from "@iconify/vue";
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from "vue";

interface PopoverMenuItem {
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

interface Props {
  items: PopoverMenuItem[];
  triggerIcon?: string;
  placement?:
    | "left"
    | "top"
    | "right"
    | "bottom"
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right";
  stayOpen?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PopoverMenu',
  props: {
    items: { default: () => [] },
    triggerIcon: { default: "mdi:dots-horizontal" },
    placement: { default: "bottom" },
    stayOpen: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const itemsArray = computed(() => {
  return Array.isArray(props.items) ? props.items : [];
});

const isOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const dropdownStyle = ref<{
  position: "fixed";
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  zIndex: number;
}>({
  position: "fixed",
  zIndex: 10060,
});

const updateDropdownPosition = () => {
  if (!triggerRef.value || !dropdownRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const spacing = 4;
  const dropdownHeight = dropdownRef.value.offsetHeight || 200;
  const dropdownWidth = dropdownRef.value.offsetWidth || 140;

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  let top: string | undefined;
  let bottom: string | undefined;
  let left: string | undefined;
  let right: string | undefined;

  if (
    props.placement === "bottom" ||
    props.placement === "bottom-left" ||
    props.placement === "bottom-right"
  ) {
    const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;
    if (shouldShowAbove) {
      bottom = `${window.innerHeight - rect.top + spacing}px`;
    } else {
      top = `${rect.bottom + spacing}px`;
    }
  } else if (
    props.placement === "top" ||
    props.placement === "top-left" ||
    props.placement === "top-right"
  ) {
    const shouldShowBelow = spaceAbove < dropdownHeight + spacing && spaceBelow > spaceAbove;
    if (shouldShowBelow) {
      top = `${rect.bottom + spacing}px`;
    } else {
      bottom = `${window.innerHeight - rect.top + spacing}px`;
    }
  } else {
    top = `${rect.bottom + spacing}px`;
  }

  if (props.placement === "bottom-right" || props.placement === "top-right") {
    right = `${window.innerWidth - rect.right}px`;
  } else if (props.placement === "bottom-left" || props.placement === "top-left") {
    left = `${rect.left}px`;
  } else if (props.placement === "right") {
    left = `${rect.right + spacing}px`;
    top = `${rect.top}px`;
  } else if (props.placement === "left") {
    right = `${window.innerWidth - rect.left + spacing}px`;
    top = `${rect.top}px`;
  } else {
    right = `${window.innerWidth - rect.right}px`;
  }

  dropdownStyle.value = {
    position: "fixed",
    top,
    bottom,
    left,
    right,
    zIndex: 10060,
  };
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => {
      updateDropdownPosition();
    });
  }
};

const handleItemClick = (item: PopoverMenuItem, event: MouseEvent) => {
  event.stopPropagation();
  if (!item.disabled) {
    item.onClick();
    if (!props.stayOpen) {
      isOpen.value = false;
    }
  }
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  if (
    menuRef.value &&
    !menuRef.value.contains(target) &&
    dropdownRef.value &&
    !dropdownRef.value.contains(target)
  ) {
    isOpen.value = false;
  }
};

watch(isOpen, (newVal) => {
  if (newVal) {
    nextTick(() => {
      updateDropdownPosition();
    });
  }
});

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "popover-menu",
    ref_key: "menuRef",
    ref: menuRef
  }, [
    _createElementVNode("div", {
      class: "popover-menu__trigger",
      ref_key: "triggerRef",
      ref: triggerRef,
      onClick: toggle
    }, [
      _createVNode(_unref(Icon), { icon: __props.triggerIcon }, null, 8, ["icon"])
    ], 512),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(_Transition, { name: "popover" }, {
        default: _withCtx(() => [
          (isOpen.value && itemsArray.value.length > 0)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                ref_key: "dropdownRef",
                ref: dropdownRef,
                class: "popover-menu__dropdown",
                style: _normalizeStyle(dropdownStyle.value)
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(itemsArray.value, (item, index) => {
                  return (_openBlock(), _createElementBlock("a", {
                    key: index,
                    class: _normalizeClass(["popover-menu__item", {
              'popover-menu__item--disabled': item.disabled,
              'popover-menu__item--danger': item.danger,
            }]),
                    onClick: ($event: any) => (handleItemClick(item, $event))
                  }, [
                    (item.icon)
                      ? (_openBlock(), _createBlock(_unref(Icon), {
                          key: 0,
                          icon: item.icon,
                          class: "popover-menu__item-icon"
                        }, null, 8, ["icon"]))
                      : _createCommentVNode("", true),
                    _createElementVNode("span", null, _toDisplayString(item.label), 1)
                  ], 10, _hoisted_1))
                }), 128))
              ], 4))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]))
  ], 512))
}
}

})